import React, { FC, useCallback, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_TOP_DOWN_RATES } from "~/graphql/query";
import { UPDATE_MATTER_TEAM } from "~/graphql/mutation/update-matter-team";
import { UPDATE_WORK, UPDATE_MANY_WORK } from "~/graphql/mutation";
import {
  TopDownPricing,
  TeamMembership,
  Work,
  PricingTarget,
  FeeEarnerLevel,
} from "~/design-system";
import { useMutation } from "../../hooks";
import { GpmThreshold } from "~/design-system";
import { TeamMembershipPartsFragment } from "~/gql/graphql";

import styles from "./top-down-pricing-container.module.css";

const { ["container"]: containerClassName, ["body-section"]: bodySectionClassName } = styles as any;

type TopDownPricingContainerProps = {
  matterId: number;
  currency: { symbol: string; code: string };
  gpmThreshold: GpmThreshold;
  clientId?: number;
  organisationalUnitId?: number;
  topDownRates: TeamMembershipPartsFragment[];
  topDownWork: Work[];
  pricingTarget: PricingTarget;
  topDownFeeTarget: number;
  onTopDownFeeTargetChange: (feeTarget: number | null) => void;
  topDownHoursTarget: number;
  onTopDownHoursTargetChange: (hoursTarget: number | null) => void;
  topDownRateDiscount: number;
  onTopDownRateDiscountChange: (rateDiscount: number | null) => void;
  onFeeEarnerLevelRateChange: (oldRateId: number, newRateId: number) => Promise<void>;
  onRemoveFeeEarnerLevel: (teamMembershipId: number, workIds: number[]) => Promise<void>;
  onAddFeeEarnerLevel: (
    feeEarnerLevelId: number,
    rateId: number
  ) => Promise<{ teamMembershipId?: number; work?: Work }>;
  onAddFeeEarner?: (
    feeEarnerId: number | null,
    feeEarnerLevelId: number | null,
    rateId: number
  ) => Promise<{ teamMembershipId?: number; work?: Work }>;
  feeEarnerLevels: FeeEarnerLevel[];
  refreshTrigger: number;
};

export const TopDownPricingContainer: FC<TopDownPricingContainerProps> = ({
  matterId,
  currency,
  gpmThreshold,
  clientId,
  organisationalUnitId,
  topDownRates,
  topDownWork,
  pricingTarget,
  topDownFeeTarget,
  onTopDownFeeTargetChange,
  topDownHoursTarget,
  onTopDownHoursTargetChange,
  topDownRateDiscount,
  onTopDownRateDiscountChange,
  onFeeEarnerLevelRateChange,
  onRemoveFeeEarnerLevel,
  onAddFeeEarnerLevel,
  onAddFeeEarner,
  feeEarnerLevels,
  refreshTrigger,
}) => {
  const [updateWork] = useMutation(UPDATE_WORK);
  const [updateManyWork] = useMutation(UPDATE_MANY_WORK);
  const [updateMatterTeam] = useMutation(UPDATE_MATTER_TEAM);
  const [topDownRatesSorted, setTopDownRatesSorted] = useState<TeamMembershipPartsFragment[]>([]);

  const { data: ratesData = undefined, loading: loadingRates } = useQuery(GET_TOP_DOWN_RATES, {
    variables: {
      filter: {
        clientId,
        organisationalUnitId,
        currencyCode: currency.code,
        matterId,
      },
    },
  });

  useEffect(() => {
    const getLevelOrder = (teamMembership: TeamMembershipPartsFragment) =>
      teamMembership.feeEarner?.feeEarnerLevel?.order ??
      teamMembership.rate?.feeEarner?.feeEarnerLevel?.order ??
      teamMembership.rate?.feeEarnerLevel?.order ??
      0;

    const isFeeEarnerSpecific = (teamMembership: TeamMembershipPartsFragment) =>
      Boolean(teamMembership.feeEarner ?? teamMembership.rate?.feeEarner);

    setTopDownRatesSorted(
      [...topDownRates].sort((a, b) => {
        const specificDelta = Number(isFeeEarnerSpecific(a)) - Number(isFeeEarnerSpecific(b));
        if (specificDelta !== 0) {
          return specificDelta;
        }

        const levelDelta = getLevelOrder(a) - getLevelOrder(b);
        if (levelDelta !== 0) {
          return levelDelta;
        }

        if (isFeeEarnerSpecific(a)) {
          const feeEarnerNameA = a.feeEarner?.name ?? a.rate?.feeEarner?.name ?? "";
          const feeEarnerNameB = b.feeEarner?.name ?? b.rate?.feeEarner?.name ?? "";
          if (feeEarnerNameA !== feeEarnerNameB) {
            return feeEarnerNameA.localeCompare(feeEarnerNameB);
          }
        }

        return (a.rateId ?? 0) - (b.rateId ?? 0);
      })
    );
  }, [topDownRates]);
  const topDownRateOptions = ratesData?.getTopDownRates || [];

  const handleUpdateManyWorkHours = useCallback(
    (
      data: {
        workId: number;
        hours: number;
        workEffort: number;
        rateDiscount: number;
      }[]
    ) => {
      updateManyWork({
        variables: {
          data: data.map(({ workId, hours, workEffort, rateDiscount }) => ({
            id: workId,
            payload: {
              timeSingle: { set: hours },
              workEffort: { set: workEffort },
              topDownRateDiscount: { set: rateDiscount },
            },
          })),
        },
      });
    },
    [updateManyWork]
  );

  return (
    <div className={containerClassName}>
      <div className={bodySectionClassName}>
        <TopDownPricing
          currency={currency}
          matterId={matterId}
          clientId={clientId}
          organisationalUnitId={organisationalUnitId}
          gpmThreshold={gpmThreshold}
          feeEarnerLevels={feeEarnerLevels}
          topDownRates={topDownRatesSorted as TeamMembership[]}
          topDownWork={topDownWork}
          topDownRateOptions={topDownRateOptions}
          pricingTarget={pricingTarget}
          topDownFeeTarget={topDownFeeTarget}
          onTopDownFeeTargetChange={onTopDownFeeTargetChange}
          topDownHoursTarget={topDownHoursTarget}
          onTopDownHoursTargetChange={onTopDownHoursTargetChange}
          topDownRateDiscount={topDownRateDiscount}
          onTopDownRateDiscountChange={onTopDownRateDiscountChange}
          onFeeEarnerLevelRateChange={async (oldRateId, newRateId, workId) => {
            await onFeeEarnerLevelRateChange(oldRateId, newRateId);
            if (workId !== null) {
              await updateWork({
                variables: {
                  workId,
                  data: {
                    selectedRate: { connect: { id: newRateId } },
                  },
                },
              });
            }
          }}
          onUpdateWork={(data) => {
            updateWork({
              variables: {
                workId: data.workId,
                data: {
                  ...(data.timeSingle != null && { timeSingle: { set: data.timeSingle } }),
                },
              },
            });
          }}
          onFeeEarnerRateChange={async (feeEarnerId, oldRateId, newRateId, workId) => {
            await updateMatterTeam({
              variables: {
                matterId,
                teamMembersToAdd: [
                  {
                    rateId: newRateId,
                    feeEarnerId,
                  },
                ],
                teamMembersToRemove: [
                  {
                    rateId: oldRateId,
                    feeEarnerId,
                  },
                ],
              },
            });
            if (workId !== null) {
              await updateWork({
                variables: {
                  workId,
                  data: {
                    selectedRate: { connect: { id: newRateId } },
                  },
                },
              });
            }
          }}
          onUpdateManyWorkHours={handleUpdateManyWorkHours}
          onRemoveFeeEarnerLevel={onRemoveFeeEarnerLevel}
          onAddFeeEarnerLevel={async (feeEarnerLevelId) => {
            const newRate = topDownRateOptions.find(
              (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevelId
            )?.rates[0];
            if (newRate) {
              return onAddFeeEarnerLevel(feeEarnerLevelId, newRate.id);
            }
            return { teamMembershipId: undefined, work: undefined };
          }}
          onAddFeeEarner={
            onAddFeeEarner
              ? async (feeEarnerId, feeEarnerLevelId, rateId) => {
                  return onAddFeeEarner(feeEarnerId, feeEarnerLevelId, rateId);
                }
              : undefined
          }
          loadingRates={loadingRates}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </div>
  );
};
